import { creatorbase } from '@round/api';
import { getTableMetaHelper } from '@round/ui-kit';
import { CellContext } from '@tanstack/react-table';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { TooltipOnOverflow } from '@round/whitelabel-ui';
import { TableInput } from '@round/whitelabel-ui';
import styles from './ContactDetailsCell.module.css';
import { showNotification } from 'helpers';

type Row = creatorbase.Post;

type Context<T extends Row> = CellContext<T, string | undefined>;

const getTableMeta = getTableMetaHelper<BasePostTableMeta<Row>>();

const ContactDetailsCell = <T extends Row>({ table, row, getValue }: Context<T>) => {
    const initialValue = getValue();
    const [contactDetails, setContactDetails] = useState(initialValue || '');

    const [isInputFocused, setIsInputFocused] = useState(false);

    const { isLoading, updatePost } = getTableMeta(table);

    const handleUpdate = async (value: string) => {
        const errorMessage = 'Failed to update contact details';
        if (!updatePost) {
            return;
        }

        if (value === initialValue) {
            return;
        }

        try {
            const response = await updatePost(row.original.id, {
                contact_details: value,
            });

            if (response.status === 200) {
                setContactDetails(response.data.contact_details?.toString() || '');
                showNotification('Contact details updated successfully', 'info');
                return;
            }

            showNotification(errorMessage, 'error');
            setContactDetails(initialValue?.toString() || '');
        } catch {
            showNotification(errorMessage, 'error');
            setContactDetails(initialValue?.toString() || '');
        }
    };

    if (isLoading) {
        return <Skeleton />;
    }

    return (
        <TooltipOnOverflow tooltip={contactDetails || undefined} disabled={isInputFocused}>
            <TableInput
                value={isInputFocused ? contactDetails : contactDetails || '-'}
                onChange={(e) => setContactDetails(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                    }
                }}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => {
                    handleUpdate(contactDetails);
                    setIsInputFocused(false);
                }}
                className={styles.input}
            />
        </TooltipOnOverflow>
    );
};

export default ContactDetailsCell;
